import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, Maximize2, Minimize2, GripHorizontal, Target } from 'lucide-react';
import { StickyNoteProps } from '@/types';
import { useStickyNote } from '@/hooks/useStickyNote';
import { TaskItem } from './TaskItem';
import clsx from 'clsx';

const MAX_TASKS = 3;

export const StickyNote: React.FC<StickyNoteProps> = ({ className }) => {
  const { state, actions } = useStickyNote();
  const [newTaskContent, setNewTaskContent] = useState('');
  const [isAddingTask, setIsAddingTask] = useState(false);

  // 如果便签不可见，不渲染
  if (!state.isVisible) {
    return null;
  }

  // 添加新任务
  const handleAddTask = () => {
    const content = newTaskContent.trim();
    if (content && state.tasks.length < MAX_TASKS) {
      actions.addTask(content);
      setNewTaskContent('');
      setIsAddingTask(false);
    }
  };

  // 取消添加任务
  const handleCancelAdd = () => {
    setNewTaskContent('');
    setIsAddingTask(false);
  };

  // 处理键盘事件
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTask();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelAdd();
    }
  };

  // 计算完成的任务数
  const completedCount = state.tasks.filter(task => task.completed).length;
  const totalCount = state.tasks.length;

  return (
    <motion.div
      drag
      dragMomentum={false}
      onDragEnd={(_, info) => {
        const newPosition = {
          x: Math.max(0, Math.min(window.innerWidth - 320, state.position.x + info.offset.x)),
          y: Math.max(0, Math.min(window.innerHeight - 200, state.position.y + info.offset.y))
        };
        actions.updatePosition(newPosition);
      }}
      style={{
        x: state.position.x,
        y: state.position.y
      }}
      className={clsx(
        'fixed z-50 select-none cursor-move',
        'w-80 bg-gradient-to-br from-yellow-100 to-yellow-50',
        'border-2 border-yellow-200 rounded-xl shadow-lg backdrop-blur-sm',
        className
      )}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* 便签头部 */}
      <div className="flex items-center justify-between p-3 border-b border-yellow-200 bg-yellow-200/50">
        <div className="flex items-center gap-2">
          <div className="text-yellow-600">
            <Target size={18} />
          </div>
          <h3 className="font-medium text-yellow-800 text-sm">今日专注</h3>
          {totalCount > 0 && (
            <span className="text-xs text-yellow-600 bg-yellow-100 px-2 py-0.5 rounded-full">
              {completedCount}/{totalCount}
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-1">
          {/* 拖拽手柄 */}
          <div className="text-yellow-600 cursor-move p-1">
            <GripHorizontal size={16} />
          </div>
          
          {/* 最小化/最大化按钮 */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={actions.toggleMinimize}
            className="text-yellow-600 hover:text-yellow-800 transition-colors p-1"
            title={state.isMinimized ? '展开' : '收起'}
          >
            {state.isMinimized ? <Maximize2 size={14} /> : <Minimize2 size={14} />}
          </motion.button>
          
          {/* 关闭按钮 */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={actions.toggleVisibility}
            className="text-yellow-600 hover:text-red-500 transition-colors p-1"
            title="隐藏便签 (T键)"
          >
            <Minus size={14} />
          </motion.button>
        </div>
      </div>

      {/* 便签内容 */}
      <AnimatePresence>
        {!state.isMinimized && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="p-3 cursor-default">
              {/* 任务列表 */}
              <div className="space-y-1 mb-3">
                <AnimatePresence>
                  {state.tasks.map((task) => (
                    <TaskItem
                      key={task.id}
                      task={task}
                      isEditing={state.isEditing === task.id}
                      onToggle={actions.toggleTask}
                      onUpdate={actions.updateTask}
                      onDelete={actions.deleteTask}
                      onStartEdit={actions.startEditing}
                      onStopEdit={actions.stopEditing}
                    />
                  ))}
                </AnimatePresence>
              </div>

              {/* 添加任务区域 */}
              {state.tasks.length < MAX_TASKS && (
                <div className="border-t border-yellow-200 pt-3">
                  {isAddingTask ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={newTaskContent}
                        onChange={(e) => setNewTaskContent(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onBlur={handleAddTask}
                        placeholder="输入任务内容..."
                        className="flex-1 px-2 py-1 text-sm bg-white border border-gray-300 rounded focus:outline-none focus:border-blue-400"
                        maxLength={50}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <motion.button
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setIsAddingTask(true)}
                      className="w-full flex items-center justify-center gap-2 p-2 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-100/50 rounded-lg border-2 border-dashed border-yellow-300 hover:border-yellow-400 transition-colors"
                    >
                      <Plus size={16} />
                      <span className="text-sm">添加任务</span>
                    </motion.button>
                  )}
                </div>
              )}

              {/* 空状态提示 */}
              {state.tasks.length === 0 && !isAddingTask && (
                <div className="text-center py-4">
                  <div className="text-yellow-600 mb-2">
                    <Target size={24} className="mx-auto opacity-50" />
                  </div>
                  <p className="text-sm text-yellow-600 mb-3">
                    添加今日要专注的事项吧
                  </p>
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setIsAddingTask(true)}
                    className="px-4 py-2 bg-yellow-200 hover:bg-yellow-300 text-yellow-800 rounded-lg text-sm font-medium transition-colors"
                  >
                    开始添加
                  </motion.button>
                </div>
              )}

              {/* 任务已满提示 */}
              {state.tasks.length >= MAX_TASKS && (
                <div className="text-center py-2">
                  <p className="text-xs text-yellow-600">
                    专注于这{MAX_TASKS}件事就足够了 ✨
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 底部提示 */}
      {!state.isMinimized && (
        <div className="px-3 pb-2">
          <p className="text-xs text-yellow-600/80 text-center">
            双击任务可编辑 • T键隐藏便签 • 每日自动重置
          </p>
        </div>
      )}
    </motion.div>
  );
};